import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Download, Zap, TrendingUp, Infinity } from 'lucide-react';
import { AnalysisResults } from '@/components/results/AnalysisResults';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Modal, ModalFooter } from '@/components/ui/Modal';
import { useAuth } from '@/hooks/useAuth';
import { useCompanyBranding } from '@/hooks/useCompanyBranding';
import { supabase } from '@/utils/supabase';
import { analytics } from '@/utils/analytics';
import { DPEAPI } from '@/services/api';

export const ResultsPage: React.FC = () => {
  const { id: dpeId } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { isProUser } = useCompanyBranding();

  const [results, setResults] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [questionnaireId, setQuestionnaireId] = useState<string | null>(null);
  const [isLegacyDPE, setIsLegacyDPE] = useState<boolean>(false);
  
  // Load results from location state or API
  useEffect(() => {
    if (authLoading || !user?.id) {
      return;
    }
    
    const loadResults = async () => {
      try {
        setLoading(true);
        setError(null);
        
        await loadResultsFromDatabase();
      } catch (error) {
        console.error('Error loading results:', error);
        setError('Erreur lors du chargement des résultats. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };
    
    loadResults();
  }, [dpeId, user?.id, authLoading]);
  
  const loadResultsFromDatabase = async () => {
    if (!dpeId || !user?.id) {
      throw new Error('DPE ID ou utilisateur manquant');
    }
    
    console.log('🔍 Loading results from database for DPE:', dpeId);
    
    // 1. Récupérer les données DPE
    const { data: dpeData, error: dpeError } = await supabase
      .from('dpe_data')
      .select('*')
      .eq('id', dpeId)
      .eq('user_id', user.id)
      .single();
    
    if (dpeError || !dpeData) {
      throw new Error('DPE non trouvé ou accès non autorisé');
    }
    
    // Les champs ADEME (conso_chauffage_ef, deperditions_*, etc.) sont optionnels selon le DPE.
    // La page scénarios travaux gère les données manquantes via enrichDPEWithEstimates().
    // On ne masque plus le bouton sur la base de champs ADEME absents.
    const isLegacy = false;
    
    setIsLegacyDPE(isLegacy);
    
    console.log('✅ DPE data loaded:', {
      id: dpeData.id,
      status: dpeData.status,
      energy_class: dpeData.energy_class,
      // Debug des champs critiques
      deperditions_baies_vitrees: dpeData.deperditions_baies_vitrees,
      deperditions_murs: dpeData.deperditions_murs,
      deperditions_planchers_hauts: dpeData.deperditions_planchers_hauts,
      besoin_chauffage: dpeData.besoin_chauffage,
      besoin_ecs: dpeData.besoin_ecs,
      isLegacyMode: isLegacy
    });
    
    // 2. Récupérer l'analyse DPE (dpe_analyses)
    const { data: analysisData, error: analysisError } = await supabase
      .from('dpe_analyses')
      .select('*')
      .eq('dpe_id', dpeId)
      .order('created_at', { ascending: false })
      .limit(1);
    
    let analysisResults = {};
    if (!analysisError && analysisData && analysisData.length > 0) {
      const analysis = analysisData[0];

      analysisResults = {
        factorsAnalysis: analysis.factors_analysis || {},
        errorsDetected: analysis.errors_detected || [],
        opportunities: analysis.opportunities || [],
        confidenceScore: analysis.confidence_score ?? 85,
        rulesApplied: analysis.rules_applied || [],
        energyClass: analysis.energy_class,
        climateClass: analysis.climate_class,
        totalPrimaryEnergy: analysis.total_primary_energy,
        totalEmissions: analysis.total_emissions
      };

      console.log('✅ Analysis data loaded:', {
        analysisId: analysis.id,
        confidenceScore: analysisResults.confidenceScore,
        confidenceScoreRaw: analysis.confidence_score,
        errorsCount: analysisResults.errorsDetected.length,
        opportunitiesCount: analysisResults.opportunities.length
      });
    } else {
      console.warn('⚠️ No analysis data found for DPE:', dpeId);
    }
    
    // 3. Récupérer la dernière session de questionnaire (complétée ou en cours)
    // On priorise COMPLETED, puis IN_PROGRESS, puis PENDING
    const { data: sessionData, error: sessionError } = await supabase
      .from('questionnaire_sessions')
      .select('*')
      .eq('dpe_id', dpeId)
      .eq('user_id', user.id)
      .in('status', ['COMPLETED', 'IN_PROGRESS', 'PENDING'])
      .order('created_at', { ascending: false })
      .limit(1);
    
    let finalRecommendations = {};
    let userAnswers = [];
    let sessionId = null;
    
    if (!sessionError && sessionData && sessionData.length > 0) {
      const session = sessionData[0];
      sessionId = session.id;
      setQuestionnaireId(sessionId);
      
      try {
        const rawRecs = session.final_recommendations_json;
        if (typeof rawRecs === 'string') {
          finalRecommendations = JSON.parse(rawRecs);
        } else if (typeof rawRecs === 'object' && rawRecs !== null) {
          finalRecommendations = rawRecs;
        } else {
          finalRecommendations = {};
        }
      } catch (error) {
        console.error('Error parsing final_recommendations_json:', error);
        finalRecommendations = {};
      }
      
      try {
        const rawAnswers = session.answers_json;
        if (typeof rawAnswers === 'string') {
          userAnswers = JSON.parse(rawAnswers);
        } else if (Array.isArray(rawAnswers)) {
          userAnswers = rawAnswers;
        } else {
          userAnswers = [];
        }
      } catch (error) {
        console.error('Error parsing answers_json:', error);
        userAnswers = [];
      }
      
      console.log('✅ Questionnaire session loaded:', {
        sessionId: session.id,
        status: session.status,
        answersCount: userAnswers.length,
        hasRecommendations: Object.keys(finalRecommendations).length > 0,
        confidenceScoreFromRecommendations: finalRecommendations.confidence_score
      });
    } else {
      console.warn('⚠️ No completed questionnaire session found for DPE:', dpeId);
    }

    // 4. Construire l'objet results complet
    const combinedResults = {
      errorsDetected: analysisResults.errorsDetected || [],
      opportunities: analysisResults.opportunities || [],
      factorsAnalysis: analysisResults.factorsAnalysis || {},
      rulesApplied: analysisResults.rulesApplied || [],

      final_recommendations: finalRecommendations,

      recommendations: [
        ...(finalRecommendations.immediate_actions || []),
        ...(finalRecommendations.short_term_actions || []),
        ...(finalRecommendations.medium_term_actions || []),
        ...(finalRecommendations.long_term_actions || [])
      ],

      sessionData: {
        dpe_data: dpeData,
        created_at: dpeData.created_at
      },
      userAnswers: userAnswers,
      dpeData: dpeData,
      confidenceScore: finalRecommendations.confidence_score ?? analysisResults.confidenceScore ?? 85,

      analysisDate: dpeData.created_at || new Date().toISOString(),

      data: {
        classe: dpeData.energy_class || 'D',
        classeClimat: dpeData.climate_class || 'C',
        consommation: dpeData.conso_5_usages_par_m2_ep || dpeData.primary_energy_consumption || 0,
        emissions: dpeData.emission_ges_5_usages_par_m2 || dpeData.greenhouse_gas_emissions || 0
      },

      hasQuestionnaireSession: !!sessionId,
      questionnaireSessionId: sessionId,
      isLegacyDPE: isLegacy // ✅ NOUVEAU : Ajouter aux résultats
    };

    console.log('✅ Combined results prepared:', {
      hasRecommendations: combinedResults.recommendations.length > 0,
      hasQuestionnaireSession: combinedResults.hasQuestionnaireSession,
      confidenceScore: combinedResults.confidenceScore,
      confidenceScoreSource: finalRecommendations.confidence_score !== undefined ? 'finalRecommendations' :
                             analysisResults.confidenceScore !== undefined ? 'analysisResults' : 'default',
      isLegacyDPE: isLegacy
    });
    
    setResults(combinedResults);
  };
  
  const handleExportPDF = () => {
    analytics.trackEvent({
      category: 'analysis',
      action: 'export_pdf',
      label: dpeId || '',
      custom_parameters: {
        user_type: isProUser ? 'pro' : 'standard'
      }
    });
  };
  
  const handleShare = () => {
    const shareUrl = `${window.location.origin}/shared/dpe/${dpeId}`;
    setShareUrl(shareUrl);
    setShowShareModal(true);
  };
  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
    alert('Lien copié dans le presse-papier');  
  };
  
  const handleModifyAnswers = async () => {
    if (questionnaireId) {
      navigate(`/analysis/${dpeId}/questions/${questionnaireId}`);
      return;
    }
    // Aucune session trouvee : on en cree/recupere une via l'API
    try {
      const response = await DPEAPI.getQuestions(dpeId!);
      if (response.success && response.session_id) {
        navigate(`/analysis/${dpeId}/questions/${response.session_id}`);
      } else {
        navigate(`/analysis/${dpeId}`);
      }
    } catch {
      navigate(`/analysis/${dpeId}`);
    }
  };
  
  const handleRefreshResults = async () => {
    try {
      setLoading(true);
      await loadResultsFromDatabase();
    } catch (error) {
      console.error('Error refreshing results:', error);
      setError('Erreur lors du rechargement des résultats');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToScenarios = () => {
    navigate(`/analysis/${dpeId}/scenarios`);
  };
  
  if (authLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Chargement de l'authentification..." />
      </div>
    );
  }
  
  if (!user) {
    navigate('/auth');
    return null;
  }
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Chargement des résultats..." />
      </div>
    );
  }
  
  if (error || !results) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Erreur de chargement
        </h2>
        <p className="text-gray-600 mb-6">
          {error || "Une erreur est survenue lors du chargement des résultats."}
        </p>
        <Button
          variant="primary"
          onClick={() => navigate(`/analysis/${dpeId}`)}
        >
          Retour à l'analyse
        </Button>
      </div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto">
      <Helmet>
        <title>Résultats et recommandations - DPEWizard</title>
        <meta name="description" content="Découvrez les résultats de votre analyse DPE avec des recommandations personnalisées pour améliorer votre performance énergétique." />
      </Helmet>
      
      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4"
          onClick={() => navigate(`/analysis/${dpeId}`)}
          icon={<ArrowLeft className="w-4 h-4 mr-2" />}
        >
          Retour à l'analyse
        </Button>
        
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Résultats et recommandations
          </h1>
          <p className="text-gray-600">
            Analyse complète de votre DPE avec recommandations personnalisées
          </p>
        </div>
      </div>
      
      {/* ✅ MODIFIÉ : Passer isLegacyDPE au composant */}
      <AnalysisResults
        dpeId={dpeId || ''}
        analysisId={results?.analysisId || ''}
        results={results}
        onExportPDF={handleExportPDF}
        onShare={handleShare}
        onModifyAnswers={handleModifyAnswers}
        isLegacyDPE={isLegacyDPE}
      />

      {/* Teaser PDF white-label pour les non-Pro */}
      {user && !isProUser && (user.subscription_status === 'active' || user.subscription_status === 'trialing') && (
        <div className="mt-6 rounded-xl border border-purple-200 bg-purple-50 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-1">Plan Pro Agence</p>
            <h4 className="text-base font-bold text-gray-900 mb-1">
              Envoyez ce rapport à vos clients avec votre logo
            </h4>
            <p className="text-sm text-gray-600">
              Avec le plan Pro Agence, chaque rapport PDF sort automatiquement avec votre logo et le nom de votre entreprise — prêt à remettre en rendez-vous.
            </p>
          </div>
          <div className="shrink-0">
            <button
              onClick={() => {
                analytics.trackEvent({
                  category: 'conversion',
                  action: 'click_pdf_whitelabel_teaser',
                  label: dpeId || ''
                });
                navigate('/pro#pricing');
              }}
              className="inline-flex items-center px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 transition-colors whitespace-nowrap"
            >
              Voir le plan Pro Agence →
            </button>
          </div>
        </div>
      )}

      {/* Upsell abonnement pour les non-abonnés */}
      {user && user.subscription_status !== 'active' && user.subscription_status !== 'trialing' && (
        <div className="mt-10 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 text-white p-8 shadow-xl">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <Infinity className="w-5 h-5 text-blue-200" />
                <span className="text-blue-200 text-sm font-medium uppercase tracking-wide">
                  Analyses illimitées
                </span>
              </div>
              <h3 className="text-2xl font-bold mb-2">
                Vous avez d'autres biens à analyser ?
              </h3>
              <p className="text-blue-100 mb-4 max-w-lg">
                À partir de 2 analyses par mois, l'abonnement revient moins cher qu'un achat à l'unité.
                Analyses illimitées, DPE projetés, rapports PDF — sans engagement.
              </p>
              <div className="flex flex-wrap gap-4 text-sm text-blue-200">
                <div className="flex items-center gap-1.5">
                  <TrendingUp className="w-4 h-4" />
                  <span>Analyses DPE illimitées</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Zap className="w-4 h-4" />
                  <span>Scénarios de travaux inclus</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Download className="w-4 h-4" />
                  <span>Rapports PDF complets</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-3 shrink-0">
              <div className="text-center mb-1">
                <div className="text-4xl font-bold">19,90€</div>
                <div className="text-blue-200 text-sm">/mois · sans engagement</div>
              </div>
              <Button
                variant="outline"
                size="lg"
                className="bg-white text-blue-700 border-white hover:bg-blue-50 font-semibold w-full"
                onClick={() => {
                  analytics.trackEvent({
                    category: 'conversion',
                    action: 'click_upsell_banner_results',
                    label: dpeId || ''
                  });
                  navigate('/pricing');
                }}
              >
                Passer à l'abonnement illimité
              </Button>
              <span className="text-blue-200 text-xs">Ou 190€/an — 2 mois offerts</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Share Modal */}
      <Modal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title="Partager les résultats"
        description="Partagez les résultats de votre analyse DPE"
      >
        <div className="space-y-4 py-4">
          <p className="text-sm text-gray-600">
            Utilisez le lien ci-dessous pour partager les résultats de votre analyse DPE avec d'autres personnes.
          </p>
          
          <div className="flex items-center">
            <input
              type="text"
              value={shareUrl}
              readOnly
              className="flex-1 p-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Button
              variant="primary"
              className="rounded-l-none"
              onClick={copyToClipboard}
            >
              Copier
            </Button>
          </div>
          
          <div className="flex items-center justify-center space-x-4 mt-4">
            <Button
              variant="outline"
              size="sm"
              icon={<Download className="w-4 h-4 mr-2" />}
              onClick={handleExportPDF}
            >
              Exporter PDF
            </Button>
          </div>
        </div>
        
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => setShowShareModal(false)}
          >
            Fermer
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ResultsPage;